import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSidebar } from '../../context/LayoutContext';
import { useApp } from '../../context/AppContextBeta';
import {
	ChevronDownIcon,
	EllipsisHorizontalIcon,
} from '@heroicons/react/20/solid';
import {
	Squares2X2Icon,
	QueueListIcon,
	UserGroupIcon,
	MapPinIcon,
	CalendarDaysIcon,
	ServerIcon,
	ExclamationTriangleIcon,
	DocumentIcon,
	UsersIcon,
	DocumentMagnifyingGlassIcon,
	Cog6ToothIcon,
	ArrowLeftStartOnRectangleIcon,
	BookOpenIcon,
	ShieldCheckIcon,
} from '@heroicons/react/24/outline';

import LogoMark from '../../images/logo-mark.png';
import LogoLight from '../../images/logo-light-extended.png';
import LogoDark from '../../images/logo-dark.png';
const navItems = [
	{
		icon: <Squares2X2Icon />,
		name: 'Dashboard',
		path: '/dashboard',
	},

	{
		icon: <CalendarDaysIcon />,
		name: 'Agenda',
		path: '/agenda',
	},
	{
		icon: <ServerIcon />,
		name: 'Assets',
		path: '/assets/all',
	},
	{
		icon: <BookOpenIcon />,
		name: 'Bookings',
		path: '/bookings',
	},
	{
		name: 'Tickets',
		icon: <ExclamationTriangleIcon />,
		path: '/tickets',
	},
];

const othersItems = [
	{
		icon: <DocumentIcon />,
		name: 'Contracts',
		path: '/manage/contracts',
	},
	{
		icon: <UsersIcon />,
		name: 'Directory',
		path: '/manage/directory',
	},
	{
		icon: <DocumentMagnifyingGlassIcon />,
		name: 'Documents',
		path: '/manage/documents',
	},
	{
		icon: <MapPinIcon />,
		name: 'Locations',
		path: '/manage/locations',
	},
	{
		icon: <QueueListIcon />,
		name: 'Tasks',
		path: '/manage/tasks',
	},
	{
		icon: <UserGroupIcon />,
		name: 'Team',
		path: '/manage/team',
	},
];

const getAccountItems = (isSuperAdmin) => [
	...(isSuperAdmin
		? [{ icon: <ShieldCheckIcon />, name: 'Super Admin', path: '/superadmin' }]
		: []),
	{ icon: <Cog6ToothIcon />, name: 'Settings', path: '/settings' },
	{ icon: <ArrowLeftStartOnRectangleIcon />, name: 'Logout', path: '/logout' },
];

const BetaSidebar = () => {
	const {
		isExpanded,
		isMobile,
		isMobileOpen,
		isHovered,
		setIsHovered,
		toggleMobileSidebar,
	} = useSidebar();
	const location = useLocation();
	const { role } = useApp();

	const isSuperAdmin = role?.name === 'default:superadmin';
	const accountItems = getAccountItems(isSuperAdmin);

	const [openSubmenu, setOpenSubmenu] = useState(null);
	const [subMenuHeight, setSubMenuHeight] = useState({});
	const subMenuRefs = useRef({});

	// const isActive = (path: string) => location.pathname === path;
	const isActive = useCallback(
		(path) => {
			return (
				location.pathname === path ||
				(location.pathname === '/' && path === '/dashboard')
			);
		},
		[location.pathname],
	);

	useEffect(() => {
		let submenuMatched = false;
		['main', 'manage'].forEach((menuType) => {
			const items = menuType === 'main' ? navItems : othersItems;
			items.forEach((nav, index) => {
				if (nav.subItems) {
					nav.subItems.forEach((subItem) => {
						if (isActive(subItem.path)) {
							setOpenSubmenu({
								type: menuType,
								index,
							});
							submenuMatched = true;
						}
					});
				}
			});
		});

		if (!submenuMatched) {
			setOpenSubmenu(null);
		}
	}, [location, isActive]);

	useEffect(() => {
		if (openSubmenu !== null) {
			const key = `${openSubmenu.type}-${openSubmenu.index}`;
			if (subMenuRefs.current[key]) {
				setSubMenuHeight((prevHeights) => ({
					...prevHeights,
					[key]: subMenuRefs.current[key]?.scrollHeight || 0,
				}));
			}
		}
	}, [openSubmenu]);

	const handleSubmenuToggle = (index, menuType) => {
		setOpenSubmenu((prevOpenSubmenu) => {
			if (
				prevOpenSubmenu &&
				prevOpenSubmenu.type === menuType &&
				prevOpenSubmenu.index === index
			) {
				return null;
			}
			return { type: menuType, index };
		});
	};

	const renderMenuItems = (items, menuType) => (
		<ul className="flex flex-col gap-2">
			{items.map((nav, index) => (
				<li key={nav.name}>
					{nav.subItems ? (
						<button
							onClick={() => handleSubmenuToggle(index, menuType)}
							className={`menu-item group ${
								openSubmenu?.type === menuType && openSubmenu?.index === index
									? 'menu-item-active'
									: 'menu-item-inactive'
							} cursor-pointer ${
								!isExpanded && !isHovered
									? 'lg:justify-center'
									: 'lg:justify-start'
							}`}
						>
							<span
								className={`menu-item-icon-size  ${
									openSubmenu?.type === menuType && openSubmenu?.index === index
										? 'menu-item-icon-active'
										: 'menu-item-icon-inactive'
								}`}
							>
								{nav.icon}
							</span>
							{(isExpanded || isHovered || isMobileOpen) && (
								<span className="menu-item-text">{nav.name}</span>
							)}
							{(isExpanded || isHovered || isMobileOpen) && (
								<ChevronDownIcon
									className={`ml-auto w-5 h-5 transition-transform duration-200 ${
										openSubmenu?.type === menuType &&
										openSubmenu?.index === index
											? 'rotate-180 text-brand-500'
											: ''
									}`}
								/>
							)}
						</button>
					) : (
						nav.path && (
							<Link
								to={nav.path}
								onClick={() => {
									console.log('CLICK:');
									if (isMobileOpen) {
										console.log('CLICK: mobile only');

										toggleMobileSidebar();
									}
								}}
								className={`menu-item group ${
									isActive(nav.path) ? 'menu-item-active' : 'menu-item-inactive'
								}`}
							>
								<span
									className={`menu-item-icon-size ${
										isActive(nav.path)
											? 'menu-item-icon-active'
											: 'menu-item-icon-inactive'
									}`}
								>
									{nav.icon}
								</span>
								{(isExpanded || isHovered || isMobileOpen) && (
									<span className="menu-item-text">{nav.name}</span>
								)}
							</Link>
						)
					)}
					{nav.subItems && (isExpanded || isHovered || isMobileOpen) && (
						<div
							ref={(el) => {
								subMenuRefs.current[`${menuType}-${index}`] = el;
							}}
							className="overflow-hidden transition-all duration-300"
							style={{
								height:
									openSubmenu?.type === menuType && openSubmenu?.index === index
										? `${subMenuHeight[`${menuType}-${index}`]}px`
										: '0px',
							}}
						>
							<ul className="mt-2 space-y-1 ml-9">
								{nav.subItems.map((subItem) => (
									<li key={subItem.name}>
										<Link
											to={subItem.path}
											className={`menu-dropdown-item ${
												isActive(subItem.path)
													? 'menu-dropdown-item-active'
													: 'menu-dropdown-item-inactive'
											}`}
										>
											{subItem.name}
											<span className="flex items-center gap-1 ml-auto">
												{subItem.new && (
													<span
														className={`ml-auto ${
															isActive(subItem.path)
																? 'menu-dropdown-badge-active'
																: 'menu-dropdown-badge-inactive'
														} menu-dropdown-badge`}
													>
														new
													</span>
												)}
												{subItem.pro && (
													<span
														className={`ml-auto ${
															isActive(subItem.path)
																? 'menu-dropdown-badge-active'
																: 'menu-dropdown-badge-inactive'
														} menu-dropdown-badge`}
													>
														pro
													</span>
												)}
											</span>
										</Link>
									</li>
								))}
							</ul>
						</div>
					)}
				</li>
			))}
		</ul>
	);

	/*

      
  */

	return (
		<aside
			className={`fixed flex flex-col lg:mt-0 top-0 px-5 left-0 bg-gray-900 dark:bg-gray-900 dark:border-gray-800 text-gray-900 h-screen transition-all duration-300 ease-in-out z-20 border-r border-gray-700 
        ${
					isExpanded || isMobileOpen
						? 'w-[290px]'
						: isHovered
							? 'w-[290px] mt-16'
							: 'w-[90px] mt-16'
				}
        ${isMobileOpen ? 'z-50' : 'z-20'} 
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0`}
			onMouseEnter={() => !isExpanded && setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			{isMobileOpen ? (
				<div className="pt-8"></div>
			) : (
				<div
					className={`py-8 flex ${
						!isExpanded && !isHovered ? 'lg:justify-center' : 'justify-start'
					}`}
				>
					<Link to="/">
						{isExpanded || isHovered || isMobileOpen ? (
							<>
								<img src={LogoLight} alt="Logo" width={120} height={30} />
							</>
						) : (
							<img src={LogoMark} alt="Logo" width={32} height={32} />
						)}
					</Link>
				</div>
			)}
			<div className="flex flex-col overflow-y-auto duration-300 ease-linear no-scrollbar h-full">
				<nav className="mb-6 h-full">
					<div className="flex flex-col gap-4 justify-between h-full">
						<div>
							<div>{renderMenuItems(navItems, 'main')}</div>
							<div className="mt-6">
								<h2
									className={`mb-4 text-xs font-semibold flex leading-[20px] text-gray-400 ${
										!isExpanded && !isHovered
											? 'lg:justify-center'
											: 'justify-start'
									}`}
								>
									{isExpanded || isHovered || isMobileOpen ? 'Manage' : null}
								</h2>
								{renderMenuItems(othersItems, 'manage')}
							</div>
						</div>
						<div>{renderMenuItems(accountItems)}</div>
					</div>
				</nav>
			</div>
		</aside>
	);
};

export default BetaSidebar;
